/**
 * Build a synthetic `AvailableRef` for a clause whose raw ref isn't in the
 * catalog - typically seed-rule refs like
 * `computed:date_diff(field:registration_date, field:installation_date, days)`
 * that the catalog hooks don't enumerate.
 *
 * Without this, the `SearchableSelect` would render the "Select a field…"
 * placeholder and the user would think the clause was silently blanked,
 * making it look like the AND-group was broken after editing.
 */

import type { AvailableRef } from './types'

const COMPUTED_DATE_DIFF_RE =
  /^computed:date_diff\(\s*([^,]+?)\s*,\s*([^,]+?)\s*,\s*([a-z]+)\s*\)$/

function humanizeFieldRef(raw: string): string {
  if (raw.startsWith('field:')) {
    const code = raw.slice('field:'.length)
    return code.replace(/_/g, ' ')
  }
  if (raw.startsWith('entity:')) {
    return raw.slice('entity:'.length)
  }
  return raw
}

/**
 * Return an `AvailableRef` for a raw ref string that the catalog hooks
 * didn't supply. The intent is labelling only - the value stays the raw
 * string, so `builderToPredicate` round-trips it unchanged.
 */
export function buildFallbackRef(raw: string): AvailableRef {
  const dateDiff = COMPUTED_DATE_DIFF_RE.exec(raw)
  if (dateDiff) {
    const [, a, b, unit] = dateDiff
    return {
      ref: raw,
      label: `${unit} between ${humanizeFieldRef(a)} and ${humanizeFieldRef(b)}`,
      category: 'computed',
      type: 'number',
    }
  }

  if (raw.startsWith('computed:')) {
    return {
      ref: raw,
      label: raw.slice('computed:'.length),
      category: 'computed',
      type: 'number',
    }
  }

  return { ref: raw, label: raw, category: 'other', type: 'string' }
}
